import { useGetRelatedAdministrativeDocuments } from "@/api/artwork/artwork";
import { AppFile, fileSchema, useDeleteFile, useUpdateFile } from "@/api/file";
import * as AF from "@/components/form-items/AbstractForm";
import { Button } from "@/components/ui/button";
import { CancelButton, DeleteButton } from "@/components/ui/custom-buttons";
import {
  CustomSheetBody,
  CustomSheetContent,
  CustomSheetFooter,
  CustomSheetHeader,
} from "@/components/ui/custom-sheet";
import { Form } from "@/components/ui/form";
import { Sheet, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import { cn, getFileFromUrl } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { DownloadCloud } from "lucide-react";
import { ComponentProps, HTMLProps, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = fileSchema.pick({
  title: true,
  description: true,
});
const formDefaultValues = {
  title: "",
  description: "",
};

const AdministrativeDocumentEditDrawer = ({
  administrativeDocument,
  disabled = false,
  ...props
}: ComponentProps<typeof Sheet> & {
  administrativeDocument?: AppFile;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const fileEditer = useUpdateFile();

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: administrativeDocument
      ? formSchema.parse(administrativeDocument)
      : formDefaultValues,
    resolver: zodResolver(formSchema),
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    if (!administrativeDocument?.id) return;

    fileEditer.mutate(
      {
        ...formSchema.parse(data),
        docId: administrativeDocument.docId,
        id: administrativeDocument.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              "artworks",
              administrativeDocument.docId,
              "administrative-documents",
            ],
          });
          props.onOpenChange?.(false);
        },
      },
    );
  }

  useEffect(() => {
    form.reset(
      administrativeDocument
        ? formSchema.parse(administrativeDocument)
        : formDefaultValues,
    );
  }, [administrativeDocument, form]);

  return (
    <Sheet {...props}>
      <CustomSheetContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col h-full"
          >
            <CustomSheetHeader>
              <SheetTitle>
                {t("administrativeDocumentModifierTitle")}
              </SheetTitle>
            </CustomSheetHeader>
            <CustomSheetBody>
              <AF.Col>
                <AF.Text
                  name="title"
                  label={t("administrativeDocumentTitleLabel")}
                />
                <AF.Text
                  name="description"
                  label={t("administrativeDocumentDescriptionLabel")}
                />
              </AF.Col>
            </CustomSheetBody>
            <CustomSheetFooter>
              <SheetClose asChild>
                <CancelButton className="flex-1" />
              </SheetClose>
              <Button
                className="flex-1"
                type="submit"
                variant="default"
                disabled={disabled || !form.formState.isValid}
              >
                {t("administrativeDocumentModifierBouton")}
              </Button>
            </CustomSheetFooter>
          </form>
        </Form>
      </CustomSheetContent>
    </Sheet>
  );
};

const AdministrativeDocumentItem = ({
  item,
  className,
  disabled = false,
  onClickItem,
  ...props
}: HTMLProps<HTMLDivElement> & {
  item: AppFile;
  onClickItem?: (i: AppFile) => void;
}) => {
  const query = useQueryClient();
  const fileDeleter = useDeleteFile();

  return (
    <div
      className={cn(
        "px-6 py-4 flex justify-between items-center transition-colors hover:bg-gray-100 cursor-pointer",
        className,
      )}
      onClick={() => onClickItem?.(item)}
      {...props}
    >
      <div className="flex gap-3 items-center">
        <FileIcon className="size-6 text-gray-500" />
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium">{item.title}</span>
          <span className="text-sm text-gray-500">
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>
      <div className="flex gap-3">
        {item.publicUrl != undefined && (
          <Button
            variant="ghost"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              getFileFromUrl(item.publicUrl as string, item.title);
            }}
          >
            <DownloadCloud className=" stroke-gray-500" />
          </Button>
        )}
        <DeleteButton
          className="p-4"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            fileDeleter.mutate(item.id, {
              onSuccess: () =>
                query.invalidateQueries({
                  queryKey: [
                    "artworks",
                    item.docId,
                    "administrative-documents",
                  ],
                }),
            });
          }}
        />
      </div>
    </div>
  );
};

export const AdministrativeDocumentList = ({
  artworkId,
}: {
  artworkId: string;
}) => {
  const [editOpen, setEditOpen] = useState(false);

  const documentToEdit = useRef<AppFile>();

  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const { t } = useTranslation();

  const { data: files } = useGetRelatedAdministrativeDocuments(artworkId);

  return (
    <div className="flex flex-col border border-gray-300 rounded-xl max-h-[526px] min-h-12 justify-center overflow-y-auto">
      <AdministrativeDocumentEditDrawer
        administrativeDocument={documentToEdit.current}
        open={editOpen}
        onOpenChange={setEditOpen}
        disabled={isReader}
      />
      {files && files.length > 0 ? (
        files.map((file, i) => (
          <AdministrativeDocumentItem
            disabled={isReader}
            onClickItem={(i) => {
              documentToEdit.current = i;
              setEditOpen(true);
            }}
            key={file.id}
            item={file}
            className={i !== files.length - 1 ? "border-b border-gray-300" : ""}
          />
        ))
      ) : (
        <p className="text-center">
          {t("administrativeDocumentsAucunResultats")}
        </p>
      )}
    </div>
  );
};
